import React from 'react';
import { useNavigation, useSearchParams } from 'react-router-dom';
import {
  VerifyEmailOtp,
  VerifyEmailOtpErrorCode,
} from '../../components/VerifyEmailOtp/VerifyEmailOtp';
import { useAppDispatch, useAppSelector } from '../../stores/redux.store';
import { fetchPageState } from '../../utilities/fetch-api';
import { usePageViewedEvent } from '../../utilities/analytics/analytics-web-client';
import { addFlag } from '../../actions/flag-actions';
import { resendOtpMessages } from '../../actions/verify-email-otp-actions';
import { useEmailLinkRequest } from './useEmailLinkRequest';

const PAGE_ID = 'emailLinkOtpScreen';
const errorCodes: Record<string, VerifyEmailOtpErrorCode> = {
  invalid_otp: 'invalid_otp',
  invalid_token: 'invalid_token',
  expired_token: 'expired_token',
  rate_limit_exceeded: 'rate_limited',
};

export const emailLinkOtpLoader = ({ request }: { request: Request }) =>
  fetchPageState('/rest/email-link/verify-email/otp/state', request, {
    capabilities: ['capability-name--verify-email'],
  });

export default function EmailLinkVerifyOtpPage() {
  usePageViewedEvent(PAGE_ID);
  const email = useAppSelector(state => state.verifyEmailOtp.email);
  const [params, setParams] = useSearchParams();
  const navigation = useNavigation();
  const isNavigating = navigation.state !== 'idle';
  const dispatch = useAppDispatch();
  const { submit, isLoading, error } = useEmailLinkRequest(PAGE_ID);
  const token = params.get('token') || '';

  const onVerifyEmailOtp = async (otpCode: string) => {
    if (isNavigating) return;
    const redirectUri = await submit(
      '/rest/email-link/verify-email/otp',
      { token, otpCode },
      'redirectUri'
    );
    if (redirectUri) window.location.assign(redirectUri);
  };

  const onResendEmailOtp = async () => {
    if (isNavigating) return;
    const newToken = await submit('/rest/email-link/verify-email/otp/resend', { token }, 'token');
    if (newToken) {
      const nextParams = new URLSearchParams(params);
      nextParams.set('token', newToken);
      setParams(nextParams, { replace: true });
      dispatch(
        addFlag(
          'success',
          resendOtpMessages.resendEmailOtpSuccessTitle,
          resendOtpMessages.resendEmailOtpSuccessBody
        )
      );
    }
  };

  return (
    <VerifyEmailOtp
      pageId={PAGE_ID}
      flow="email-link"
      email={email}
      isLoading={isLoading || isNavigating}
      errorCode={error ? errorCodes[error] || 'unexpected_error' : undefined}
      onVerifyEmailOtp={onVerifyEmailOtp}
      onResendEmailOtp={onResendEmailOtp}
    />
  );
}
